import { Component, OnDestroy, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { filter, takeUntil, debounceTime, distinctUntilChanged } from "rxjs/operators";
import { CTableComponent } from "../../../../core/ui/c-table/c-table.component";
import { DigitalMemoryLogService } from "../../services/digital-memory-log.service";
import { DigitalMemoryLogActionsService } from "../../services/digital-memory-log-actions.service";
import { DigitalMemoryLogTableService } from "../../services/digital-memory-log-table.service";
import { CTableConfig } from "src/app/core/ui/c-table/c-table-config";
import { toUrlFriendlySlug } from "src/app/shared/utils/url-slug.util";

@Component({
  selector: "app-digital-memory-log-list",
  templateUrl: "./digital-memory-log-list.component.html",
})
export class DigitalMemoryLogListComponent implements OnInit, OnDestroy, AfterViewInit {
  // ViewChild
  @ViewChild("digitalMemoryList") digitalMemoryList: CTableComponent;

  // Add AfterViewInit to check if table is ready
  ngAfterViewInit(): void {
    if (this.digitalMemoryList) {
      this.refreshList();
    }
  }

  // Properties

  shipId: string;
  shipName: string;
  searchValue = "";
  searchChanged: Subject<string> = new Subject<string>();

  // Table configuration
  digitalLogOptions: CTableConfig;

  // Private properties
  private readonly destroy$ = new Subject<void>();
  private routerSubscription: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private digitalLogService: DigitalMemoryLogService,
    private actionsService: DigitalMemoryLogActionsService,
    private tableService: DigitalMemoryLogTableService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
    this.setupSearchSubscription();
    this.setupRouterSubscription();
    this.initializeTableOptions();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Initialize component properties
   */
  private initializeComponent(): void {
    this.extractRouteParameters();
  }

  /**
   * Extract ship ID and name from route parameters
   */
  private extractRouteParameters(): void {
    // Extract ship ID and name from route parameters (provided by custom matcher)
    this.shipId = this.route.snapshot.paramMap.get("shipId") || "";
    this.shipName = this.route.snapshot.paramMap.get("shipName") || "";

    // Fallback for old URL format if custom matcher didn't match
    if (!this.shipId) {
      const oldId = this.route.snapshot.paramMap.get("id");
      if (oldId) {
        this.shipId = oldId;
        this.shipName = this.route.snapshot.queryParamMap.get("name") || "";
      }
    }
  }

  /**
   * Setup search subscription
   */
  private setupSearchSubscription(): void {
    this.searchChanged
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchText: string) => {
        this.searchValue = searchText;
        this.onSearch();
        this.cdr.markForCheck();
      });
  }

  /**
   * Setup router subscription for navigation events
   */
  private setupRouterSubscription(): void {
    this.routerSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.handleNavigationEnd(event);
      });
  }

  /**
   * Handle navigation end events
   */
  private handleNavigationEnd(event: NavigationEnd): void {
    // Check if we're navigating to this list page
    if (
      event.url.includes(`overview/ship-${this.shipId}-${this.shipName}`) &&
      !event.url.includes("/create/") &&
      !event.url.includes("/edit/")
    ) {
      setTimeout(() => {
        this.refreshList();
      }, 100); // Small delay to ensure component is ready
    }
  }

  /**
   * Initialize table options
   */
  private initializeTableOptions(): void {
    const baseOptions = this.tableService.getDefaultTableOptions();

    this.digitalLogOptions = {
      ...baseOptions,
      extraData: this.tableService.createExtraData(this.actionsService, this.shipId, this.shipName, this.route, () =>
        this.refreshTable()
      ),
      refresh: this.createRefreshFunction(),
    } as CTableConfig;
  }

  /**
   * Create refresh function for table
   */
  private createRefreshFunction(): (params: any, table: any) => void {
    return (params: any, table: any) => {
      const skip = (params._page - 1) * params._pageSize;
      const limit = params._pageSize;

      this.digitalLogService
        .getMyDigitalMemoryLogs(this.shipId, this.searchValue, null, true, skip, limit)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.handleTableDataResponse(res, table);
          },
          error: error => {
            this.handleTableDataError(error, table);
          },
        });
    };
  }

  /**
   * Handle successful table data response
   */
  private handleTableDataResponse(res: any, table: any): void {
    if (res.Data && res.Data.data && res.Data.data.length) {
      const processedData = this.tableService.processDigitalMemoryLogData(res.Data.data);
      const totalCount = res.Data.total || res.Data.data.length;
      table.setData(processedData, totalCount);
    } else {
      table.setData([], 0);
      this.updateSearchingState(table);
    }
  }

  /**
   * Handle table data error
   */
  private handleTableDataError(error: any, table: any): void {
    table.setData([], 0);
    // Error handling can be added here if needed
  }

  /**
   * Update searching state based on search value
   */
  private updateSearchingState(table: any): void {
    if (table.isSearching !== undefined) {
      table.isSearching = this.searchValue && this.searchValue.trim() !== "";
    }
  }

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    setTimeout(() => {
      this.refreshList();
    });
  }

  /**
   * Create new digital memory log
   */
  createDigitalMemory(): void {
    this.digitalLogService
      .createDigitalMemoryDraft(this.shipId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.IsSuccess && res.Data.id) {
            this.navigateToCreatePage(res.Data.id);
          }
        },
        error: error => {
          // Handle error if needed
        },
      });
  }

  /**
   * Navigate to create page
   */
  private navigateToCreatePage(id: number): void {
    const urlFriendlyShipName = toUrlFriendlySlug(this.shipName, { emptyFallback: "ship" });
    this.router.navigate([`/overview/ship-${urlFriendlyShipName}-${this.shipId}/create-digital-memory-log-${id}`]);
  }

  /**
   * Handle search input change
   */
  onSearchInputChange(event: any): void {
    const search = event.target.value;
    this.searchChanged.next(search);

    // Reset isSearching flag when search is cleared
    if (this.digitalMemoryList && !search.trim()) {
      this.digitalMemoryList.isSearching = false;
    }
  }

  /**
   * Handle search action
   */
  onSearch(): void {
    if (this.digitalMemoryList && this.digitalMemoryList.refresh) {
      this.updateSearchingState(this.digitalMemoryList);
      this.digitalMemoryList.selectPage(1);
    }
  }

  /**
   * Refresh the list
   */
  private refreshList(): void {
    if (this.digitalMemoryList) {
      this.digitalMemoryList.tableData = []; // clear old data
      this.updateSearchingState(this.digitalMemoryList);
      this.digitalMemoryList.selectPage(1);
    }
  }

  /**
   * Refresh the table
   */
  private refreshTable(): void {
    if (this.digitalMemoryList && this.digitalMemoryList.refresh) {
      this.digitalMemoryList.selectPage(1);
    }
  }
}
